import React from 'react';
import {useSelector} from 'react-redux';
import {
  selectGoblinsAbilityActions,
  selectPlayers,
  selectSelectedUnit
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {
  GOBLINS_ABILITY_RESULT_COLOR,
  GOBLINS_ABILITY_SUCCESS_THRESHOLD,
  GOBLINS_FORTRESS_DAMAGE_PENALTY
} from "../../../configs/factions/goblins";
import {MINUS_SIGN} from "../../../constants/text";
import {
  CombatDuel,
  CombatEquation,
  CombatName,
  CombatOperator,
  CombatPanel,
  CombatTerm,
  CombatTotal,
  CombatUnitToken,
  CombatVerdict,
  getCombatUnitIconProps,
} from "../CombatPanel/CombatPanel";

const ABILITY_LABELS = {
  set_trap: 'Установка ловушки',
  remove_trap: 'Снятие ловушки',
  damage_building: 'Повреждение строения',
};

// Панель формулы применённой способности Гоблинов - по образцу
// SelectedUnitSeduction (тот же принцип: показывается ЛЮБОМУ игроку,
// кликнувшему по юниту, который уже применил способность в этом этапе,
// независимо от владельца юнита и от того, чей сейчас ход - см. design.md
// Decision 25), но проще: у Гоблинов почти всегда либо голый бросок д6
// (установка ловушки / повреждение обычного строения), либо вообще без
// броска (снятие ловушки), и только повреждение крепости - с вычитанием
// (roll - GOBLINS_FORTRESS_DAMAGE_PENALTY). Формула строится из уже
// сохранённой записи в goblinsAbilityActions (roll/success/damage/
// isFortressTarget/destroyed), а не пересчитывается заново
const SelectedUnitGoblinsAbility = () => {
  const selectedUnit = useSelector(selectSelectedUnit);
  const goblinsAbilityActions = useSelector(selectGoblinsAbilityActions);
  const players = useSelector(selectPlayers);

  if (!selectedUnit || !selectedUnit.hasUsedAbilityThisWarPhase) return null;

  const unitActions = goblinsAbilityActions.filter(action => action.unitId === selectedUnit.id);
  const action = unitActions[unitActions.length - 1];
  if (!action) return null;

  const isFortress = action.type === 'damage_building' && action.isFortressTarget;
  const abilityLabel = isFortress ? 'Повреждение крепости' : ABILITY_LABELS[action.type];
  const abilityIcon = action.type === 'damage_building' ? '/img/icons/building_damage.png' : '/img/icons/trap_white.png';

  const rollTerm = (
    <CombatTerm value={action.roll} icon={'/img/icons/cube.png'} iconAlt={'Бросок'} color={GOBLINS_ABILITY_RESULT_COLOR} title={'Бросок кости'}/>
  );

  let formulaNode = null;
  let totalNode = null;
  let verdictNode;

  if (action.type === 'remove_trap') {
    verdictNode = <CombatVerdict isSuccess={true}>{'Ловушка снята, получена 1 монета'}</CombatVerdict>;
  } else if (isFortress) {
    formulaNode = (
      <CombatEquation>
        {rollTerm}
        <CombatOperator>{MINUS_SIGN}</CombatOperator>
        <CombatTerm value={GOBLINS_FORTRESS_DAMAGE_PENALTY} color={GOBLINS_ABILITY_RESULT_COLOR}/>
      </CombatEquation>
    );
    totalNode = <CombatTotal value={action.damage || 0} label={'Урон'} isDamage={true}/>;
    verdictNode = action.destroyed ? <CombatVerdict isSuccess={true}>{'Крепость разрушена!'}</CombatVerdict> : null;
  } else {
    const resultText = action.success
      ? (action.type === 'damage_building'
        ? (action.destroyed ? 'Строение разрушено!' : 'Строение повреждено!')
        : 'Ловушка установлена!')
      : 'Неудача';

    // Слагаемое одно, и оно же итог: строка расчета ничего бы не добавила
    totalNode = <CombatTotal value={action.roll} label={'Бросок'} hint={`нужно ${GOBLINS_ABILITY_SUCCESS_THRESHOLD}+`}/>;
    verdictNode = <CombatVerdict isSuccess={action.success}>{resultText}</CombatVerdict>;
  }

  return (
    <CombatPanel
      key={selectedUnit.id}
      toneColor={GOBLINS_ABILITY_RESULT_COLOR}
      duel={(
        <CombatDuel
          actor={<CombatUnitToken unitType={selectedUnit.type} iconProps={getCombatUnitIconProps(selectedUnit, players)}/>}
          actionIcon={<img src={abilityIcon} alt={abilityLabel} title={abilityLabel}/>}
        />
      )}
    >
      <CombatName>{abilityLabel}</CombatName>
      {formulaNode}
      {totalNode}
      {verdictNode}
    </CombatPanel>
  );
};

export default SelectedUnitGoblinsAbility;
